// src/gp.ts —— 后台服务（TD-806 / TD-811）：API 客户端、会话、UI 原语与 403 拦截；宿主在 app.didMount 时绑定
import { ApiClient, ApiError, type CallOptions, type SessionLostDetail, type SessionLostReason } from '../../shared/src/api/client';
import { isNotReady } from '../../shared/src/api/pending';
import type { RouteKey } from '../../shared/src/api/routes';
import type { PublicConfig } from '../../shared/src/api/types';
import type { DcHost, Dict, EventBus } from '../../shared/src/dc';
import { errorText, interpolate } from '../../shared/src/errors';
import { bootIdleExpired, browserIdleEnv, clearIdleMarks, createIdleMonitor, disposeIdleEnv, normalizeIdleMinutes, type IdleEnv, type IdleLogoutReason, type IdleMonitor } from '../../shared/src/idle';
import { TOKEN_KEYS, localTokenStore, type TokenStore } from '../../shared/src/storage';
import { firstVisible } from './domains/shell';
import { canSee, type AdminMe } from './perm';
import type { AdminState, OnbState, Page } from './state';

export type ErrVars = Readonly<Record<string, string | number | undefined>>;

export interface AdminApi {
  call<T = unknown>(route: RouteKey, o?: CallOptions): Promise<T>;
  blob(route: RouteKey, o?: CallOptions): Promise<{ blob: Blob; filename: string | null }>;
  tryRefresh(): Promise<boolean>;
}

export interface AdminUi {
  toast(msg: string): void;
  copy(text: string): void;
  run<T>(key: 'login' | 'onb', fn: () => Promise<T>): Promise<T | undefined>;
  err(e: unknown, vars?: ErrVars): string;
  dict(): Dict;
}

export interface AdminSession {
  boot(): Promise<void>;
  established(token: string, opts: { persist: boolean; reason: 'boot' | 'login' }): Promise<void>;
  restricted(token: string, r: { must_change_password: boolean }): void;
  // passive：定时轮询触发（带 X-Activity: passive，不刷新服务端最后活跃）；页面切换 / 写后刷新 / 保活为主动请求
  refreshMe(opts?: { passive?: boolean }): Promise<void>;
  logout(): Promise<void>;
  // 后端已吊销全部会话（改密 {relogin:true} / 向导绑定完成）：换代、清 token、已进壳则发 session:lost、复位到登录页并 toast
  relogin(toastKey: string, reason: 'pwd_changed' | 'onboard_done'): void;
  // 空闲提示框「继续使用」：refresh 续期并重置计时（2026-10-15）
  continueIdle(): Promise<void>;
  // 宿主卸载：停空闲计时、关闭跨标签页通道
  dispose(): void;
}

export interface AdminGp {
  api: AdminApi;
  tokens: TokenStore;
  events: EventBus;
  ui: AdminUi;
  session: AdminSession;
  bind(host: DcHost<AdminState>): void;
  host(): DcHost<AdminState>;
}

export const TOAST_MS = 2200; // 原型 L634
// GET /api/admin/me 缺 session 字段（旧后端）时的空闲阈值，与后端 adminIdleMinutes 默认值一致
export const ADMIN_IDLE_DEFAULT_MINUTES = 30;

// 回登录页时 UI 复位：page / tab 回初值，下一账号由 established 按权限落地
const LOGIN_RESET = { screen: 'login', page: 'dashboard', tab: 0 } as const satisfies Partial<AdminState>;

const ONB_STEP1: OnbState = { step: 1, old: '', nw: '', nw2: '', secret: '', qr: '', code: '', err: '' };

function text(d: Dict, k: string, fallback: string): string {
  const v = d[k];
  return typeof v === 'string' ? v : fallback;
}

function landing(me: AdminMe, cur: Page): Page {
  return canSee(me, cur) ? cur : firstVisible((p) => canSee(me, p)) ?? 'dashboard';
}

// 会话终止提示（登录页 loginErr 区显示，直到用户开始输入）：空闲退出 / 超过最长在线 / 通用过期
export function sessionNotice(d: Dict, reason: string, detail?: SessionLostDetail): string {
  if (reason === 'idle_timeout') return text(d, 'idleLoggedOut', '');
  if (reason === 'max_age') {
    const n = detail?.maxHours;
    return typeof n === 'number' && n > 0 ? interpolate(text(d, 'sessionMaxAge', ''), { n }) : text(d, 'sessionExpired', '');
  }
  return '';
}

// base / tokens / idleEnv 可注入：产品默认同源 + localStorage + 浏览器空闲环境；测试注入 msw BASE、内存 token 与假空闲环境（null = 不启用）
export function createServices(events: EventBus, impl: { dict: () => Dict; base?: string; tokens?: TokenStore; idleEnv?: IdleEnv | null }): AdminGp {
  let current: DcHost<AdminState> | null = null;
  const host = (): DcHost<AdminState> => {
    if (!current) throw new Error('后台宿主尚未绑定（didMount 之前调用了服务）');
    return current;
  };
  // 会话代 epoch（与代理端 gp.session.epoch 同口径）：登录 / 进向导 / 退出 / 过期各 +1；/me 在途结果写入前代已变即丢弃（跨账号不串数据）。
  // 域内在途结果另按 table 域 HostRun 同一性（sessionGuard）丢弃
  let epoch = 0;
  let ready = false; // session:ready 已发、session:lost 未发（会话结束路径据此决定是否发 lost）
  // TD-811：persist=true 写 localStorage.gpu_at_admin；受限窗口 token（persist=false）只存内存
  const tokens = impl.tokens ?? localTokenStore(TOKEN_KEYS.admin);
  const idleEnv = impl.idleEnv === undefined ? browserIdleEnv('admin') : impl.idleEnv;
  let idle: IdleMonitor | null = null; // 进壳（session:ready）后运行；其余时刻停止
  const client = new ApiClient({
    base: impl.base ?? '',
    aud: 'admin',
    tokens,
    // 并发 401 会多次回调：只做幂等的切屏与清理，不发受保护请求
    onSessionLost: (reason: SessionLostReason, detail?: SessionLostDetail) => {
      // 进壳后的会话失效：其他标签页同一会话一起本地退出（boot 期 / 受限窗口未运行计时，不广播）
      idle?.stop(idle.running ? 'session_lost' : undefined);
      epoch++;
      ready = false;
      events.emit('session:lost', { reason });
      const h = current;
      if (!h) return;
      const notice = sessionNotice(impl.dict(), reason, detail);
      if (h.state.screen === 'login' && !h.state.sh.me && !h.state.sh.onb) {
        if (notice) h.setState({ sh: { ...h.state.sh, loginErr: notice, idleLeft: 0 } });
        return;
      }
      h.setState({ ...LOGIN_RESET, sh: { ...h.state.sh, me: null, onb: null, idleLeft: 0, ...(notice ? { loginErr: notice } : {}) } });
    },
    onAuthedRequest: () => idle?.noteRequest(),
    log: (event, detail) => console.warn('[gp:api]', event, detail),
  });

  // 本地结束会话（不再请求服务端）：空闲到期 / 其他标签页已退出。已发过 session:lost（并发 401）时不重复发
  const endLocal = (reason: SessionLostReason | 'logout', notice: string): void => {
    epoch++;
    const wasReady = ready;
    ready = false;
    tokens.clear();
    if (wasReady) events.emit('session:lost', { reason });
    const h = current;
    if (!h) return;
    h.setState({ ...LOGIN_RESET, sh: { ...h.state.sh, me: null, onb: null, idleLeft: 0, loginPwd: '', loginOtp: '', otpErr: false, loginErr: notice } });
  };

  if (idleEnv) {
    idle = createIdleMonitor('admin', idleEnv, {
      warn: (n) => {
        const h = current;
        if (h && h.state.sh.idleLeft !== n) h.setState({ sh: { ...h.state.sh, idleLeft: n } });
      },
      unwarn: () => {
        const h = current;
        if (h && h.state.sh.idleLeft !== 0) h.setState({ sh: { ...h.state.sh, idleLeft: 0 } });
      },
      // 本标签页空闲到期（监视器已广播退出）：先请服务端吊销，失败也本地退出
      expire: () => {
        void (async () => {
          try {
            if (tokens.get()) await client.call('POST /api/admin/logout');
          } catch (e) {
            console.warn('[gp:admin] 空闲退出 logout 请求失败，仍本地退出', e);
          }
          endLocal('idle_timeout', sessionNotice(impl.dict(), 'idle_timeout'));
        })();
      },
      remoteLogout: (reason: IdleLogoutReason) => endLocal(reason === 'idle_timeout' ? 'idle_timeout' : 'logout', reason === 'idle_timeout' ? sessionNotice(impl.dict(), 'idle_timeout') : ''),
      keepAlive: () => void session.refreshMe(),
      renew: () => client.renewSession(),
    });
  }

  const session: AdminSession = {
    // TD-822：忽略 props.startLoggedIn。持久 token → /me；否则试探 refresh cookie；均失败 → 登录页
    async boot() {
      const h = host();
      try {
        const cfg = await client.call<PublicConfig>('GET /api/public/config');
        h.setState({ sh: { ...h.state.sh, timezone: cfg.timezone } });
      } catch (e) {
        console.warn('[gp:admin] config 读取失败', e);
      }
      // 上次会话已空闲超时（关页期间超过阈值）：直接按空闲退出处理，不恢复会话
      if (idleEnv && bootIdleExpired(idleEnv.store, 'admin', idleEnv.now())) {
        try {
          if (tokens.get()) await client.call('POST /api/admin/logout');
        } catch (e) {
          console.warn('[gp:admin] 空闲退出 logout 请求失败，仍本地退出', e);
        }
        tokens.clear();
        clearIdleMarks(idleEnv.store, 'admin');
        if (host().state.screen === 'booting') h.setState({ screen: 'login', sh: { ...host().state.sh, loginErr: sessionNotice(impl.dict(), 'idle_timeout') } });
        return;
      }
      const ok = !!tokens.get() || (await client.tryRefresh());
      const token = tokens.get();
      if (ok && token) {
        try {
          await session.established(token, { persist: true, reason: 'boot' });
          return;
        } catch (e) {
          console.warn('[gp:admin] 会话恢复失败', e);
        }
      }
      // 只从 booting 落到登录页：会话恢复期间已因 session:lost 切到登录页等情形不再改屏
      // refresh 带原因失败（空闲 / 超过最长在线）：登录页提示原因
      const loss = ok ? null : client.lastLoss;
      if (host().state.screen === 'booting') h.setState({ screen: 'login', ...(loss ? { sh: { ...host().state.sh, loginErr: sessionNotice(impl.dict(), loss.reason, loss.detail) } } : {}) });
    },
    async established(token, { persist, reason }) {
      tokens.set(token, persist);
      const my = ++epoch;
      let me: AdminMe;
      try {
        me = await client.call<AdminMe>('GET /api/admin/me');
      } catch (e) {
        // 交互登录失败清 token（不留半截持久会话）；boot 恢复失败保留持久 token 以便刷新重试（401 由 ApiClient refresh / onSessionLost 处理，同 H5 / 代理端 boot 口径）
        if (my === epoch && reason !== 'boot') tokens.clear();
        throw e;
      }
      if (my !== epoch) return;
      const h = host();
      const page = landing(me, h.state.page);
      // adminUser 同步为本人账号：影子实例「本人」判定（admins 页 selfTip、修改密码标题）沿用该键
      h.setState({ screen: 'app', page, tab: 0, adminUser: me.account, sh: { ...h.state.sh, me, onb: null, idleLeft: 0, loginErr: '' } });
      ready = true;
      events.emit('session:ready', { reason });
      // persist=false（受限窗口不会走到这里）之外均启动空闲计时；阈值取 /me.session（旧后端缺省按默认值）
      idle?.start(normalizeIdleMinutes(me.session?.idle_minutes, ADMIN_IDLE_DEFAULT_MINUTES));
    },
    restricted(token, r) {
      idle?.stop();
      epoch++;
      ready = false;
      tokens.set(token, false); // TD-811：受限窗口 token 只存内存
      const h = host();
      h.setState({ screen: 'onboard', sh: { ...h.state.sh, me: null, onb: r.must_change_password ? { ...ONB_STEP1 } : { ...ONB_STEP1, step: 2 } } });
    },
    // G-10 第 5 条：页面切换时刷新；当前页失去 view → 跳首个有权限页
    async refreshMe(opts) {
      if (!tokens.get() || host().state.screen !== 'app') return;
      const my = epoch;
      try {
        const me = await client.call<AdminMe>('GET /api/admin/me', opts?.passive ? { passive: true } : {});
        const h = host();
        if (my !== epoch || h.state.screen !== 'app') return;
        const page = landing(me, h.state.page);
        h.setState({ page, ...(page !== h.state.page ? { tab: 0 } : {}), sh: { ...h.state.sh, me } });
        // 阈值可能已在系统设置中调整：运行中只更新阈值、不重置最后活动
        if (ready) idle?.start(normalizeIdleMinutes(me.session?.idle_minutes, ADMIN_IDLE_DEFAULT_MINUTES));
      } catch (e) {
        console.warn('[gp:admin] /me 刷新失败', e);
      }
    },
    async logout() {
      idle?.stop('logout'); // 其他标签页一起本地退出
      try {
        await client.call('POST /api/admin/logout');
      } catch (e) {
        console.warn('[gp:admin] logout 请求失败，仍本地登出', e); // A-4
      }
      epoch++;
      ready = false;
      tokens.clear();
      events.emit('session:lost', { reason: 'logout' });
      const h = host();
      h.setState({ ...LOGIN_RESET, sh: { ...h.state.sh, me: null, onb: null, idleLeft: 0, loginPwd: '', loginOtp: '', loginErr: '', otpErr: false } });
    },
    relogin(toastKey, reason) {
      idle?.stop('session_lost'); // 后端已吊销全部会话：其他标签页一起本地退出
      epoch++;
      tokens.clear();
      const wasReady = ready;
      ready = false;
      // 已进壳（壳内改密）：各域在 session:lost 复位自身切片，避免上一账号的表格 / 抽屉 / 页面带给下一账号；受限窗口从未 ready，不发
      if (wasReady) events.emit('session:lost', { reason });
      const h = host();
      h.setState({ ...LOGIN_RESET, sh: { ...h.state.sh, me: null, onb: null, idleLeft: 0 } });
      ui.toast(text(impl.dict(), toastKey, ''));
    },
    async continueIdle() {
      const ok = (await idle?.continueSession()) ?? false;
      // 续期失败由 ApiClient 回调 onSessionLost（带原因 / expired）完成退出与提示；这里只兜底关闭提示框
      if (!ok) {
        const h = current;
        if (h && h.state.sh.idleLeft !== 0) h.setState({ sh: { ...h.state.sh, idleLeft: 0 } });
      }
    },
    dispose() {
      idle?.dispose();
      disposeIdleEnv(idleEnv);
    },
  };

  // 403/1501：向导态停留向导、不刷新（C-01）；壳内刷新 /me（权限可能已变更），文案由调用方经 ui.err 映射为 toastNoPerm
  const guard = async <T>(p: Promise<T>): Promise<T> => {
    try {
      return await p;
    } catch (e) {
      if (e instanceof ApiError && e.code === 1501 && current && current.state.screen === 'app') void session.refreshMe();
      throw e;
    }
  };
  const api: AdminApi = {
    call: (route, o) => guard(client.call(route, o)),
    blob: (route, o) => guard(client.blob(route, o)),
    tryRefresh: () => client.tryRefresh(),
  };

  const ui: AdminUi = {
    toast(msg) {
      if (!msg) return;
      const h = host();
      const id = h.state.toastId + 1;
      h.setState({ toast: msg, toastId: id });
      setTimeout(() => {
        if (h.state.toastId === id) h.setState({ toast: '' });
      }, TOAST_MS);
    },
    copy(value) {
      try {
        // clipboard 不可用或拒绝时静默，toast 照常（C-01b 秘钥复制）
        navigator.clipboard?.writeText(value).catch((e: unknown) => console.warn('[gp:admin] clipboard 写入失败', e));
      } catch (e) {
        console.warn('[gp:admin] clipboard 不可用', e);
      }
      ui.toast(text(impl.dict(), 'toastCopied', ''));
    },
    async run(key, fn) {
      const h = host();
      if (h.state.sh.busy) return undefined;
      h.setState({ sh: { ...h.state.sh, busy: key } });
      try {
        return await fn();
      } finally {
        h.setState({ sh: { ...host().state.sh, busy: '' } });
      }
    },
    err(e, vars) {
      const d = impl.dict();
      if (e instanceof ApiError) {
        // 0045：1303 不再返回在册代理超限列表（over_cap / errCapAgents 废弃），只显示 err1303
        return errorText('admin', e, d, vars ?? {});
      }
      if (isNotReady(e)) return text(d, 'featurePending', '');
      console.error('[gp:admin]', e);
      return text(d, 'toastNetwork', '');
    },
    dict: impl.dict,
  };

  return {
    api,
    tokens,
    events,
    ui,
    session,
    bind(h) {
      current = h;
    },
    host,
  };
}
